import { Component, Inject, OnInit } from "@angular/core";
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  Validators,
} from "@angular/forms";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { CommonModule } from "@angular/common";
import { MatDialogModule } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatButtonModule } from "@angular/material/button";
import { MatSelectModule } from "@angular/material/select";
import { MatAutocompleteModule } from "@angular/material/autocomplete";
import { ReactiveFormsModule } from "@angular/forms";
import { AdminService } from "../../../../components/admin/services/admin.service";
import {
  debounceTime,
  delay,
  distinctUntilChanged,
  map,
  Observable,
  of,
  Subscription,
  switchMap,
} from "rxjs";
import { adIdRoles } from "../../../../app/core/classes/auth/adIdRoles";
import { MatSnackBar } from "@angular/material/snack-bar";
import { AppSettingsService } from "src/services/appsettings.service";
import { AppSettings } from "~/core/models/appsettings";

@Component({
  selector: "app-user-dialog",
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatSelectModule,
    MatAutocompleteModule,
    ReactiveFormsModule,
  ],
  templateUrl: "./user-dialog.component.html",
  styleUrls: ["./user-dialog.component.scss"],
})
export class UserDialogComponent implements OnInit {
  public userForm: FormGroup;
  public personas = adIdRoles;
  public filteredEmails!: Observable<string[]>;
  public appSettings: AppSettings | undefined;
  public buttonDisabledUntilResponse = false;
  private subscription = new Subscription();

  constructor(
    private fb: FormBuilder,
    private snackbar: MatSnackBar,
    public dialogRef: MatDialogRef<UserDialogComponent>,
    private appSettingsService: AppSettingsService,
    @Inject(MAT_DIALOG_DATA) public data: { no_access?: boolean; table_data?: Array<{ email: string }> } | undefined,
    private adminservice: AdminService
  ) {
    this.userForm = this.fb.group({
      email: [
        "",
        [Validators.required, Validators.email],
        this.duplicateEmailValidator.bind(this),
      ],
      persona: ["", Validators.required],
    });
  }

  // ngOnInit - Initializes form with app settings if access is restricted.
  public async ngOnInit(): Promise<void> {
    if (this.data?.no_access) {
      this.appSettings = await this.appSettingsService.getSettings();
      this.userForm.get("email")?.disable();
      if (this.appSettings && typeof this.appSettings.user === 'object' && this.appSettings.user !== null && 'username' in this.appSettings.user) {
        this.userForm.setValue({
          email: (this.appSettings.user as { username: string }).username,
          persona: "",
        });
      }
    }
    this.buttonDisabledUntilResponse = false;
  }

  // onEmailFocus - Triggers real-time email autocomplete suggestions.
  public onEmailFocus(): void {
    this.filteredEmails = this.userForm.get("email")!.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((value) => this.getUserEmailOnKeyup(value))
    );
  }

  // duplicateEmailValidator - Checks if entered email already exists in table data.
  public duplicateEmailValidator(control: AbstractControl): Observable<ValidationErrors | null> {
    if (!control.value) return of(null);
    if (!this.data?.table_data) return of(null);
    const emailExist = this.data.table_data.some(
      (data: { email: string }) => data.email === control.value
    );
    return of(emailExist ? { duplicate: true } : null).pipe(delay(500));
  }

  // onEmailSelected - Re-validates the email field when a value is selected from suggestions.
  public onEmailSelected(event: { option: { value: string } }): void {
    this.userForm.get("email")?.updateValueAndValidity();
  }

  // submit - Handles form submission for either access request or user addition.
  public async submit(): Promise<void> {
    const formData = this.userForm.value;
    this.buttonDisabledUntilResponse = true;

    if (this.data?.no_access && this.appSettings && typeof this.appSettings.user === 'object' && this.appSettings.user !== null && 'username' in this.appSettings.user) {
      this.subscription = (this.adminservice
        .accessPermission({
          user_email: (this.appSettings.user as { username: string }).username,
          persona: formData.persona.role_name,
        })
        .subscribe((res) => {
          this.buttonDisabledUntilResponse = false;
          this.dialogRef.close(res);
          this.snackbar.open("access requested successfully", "Ok", {
            duration: 5000,
          });
        }));

    } else {
      this.subscription = (
        this.adminservice
          .accessOperations({
            user_email: formData.email,
            group_id: formData.persona.ad_id,
            previous_group_id: "",
            operational_flag: "add",
          })
          .subscribe((res) => {
            this.buttonDisabledUntilResponse = false;
            this.dialogRef.close(res);
            this.snackbar.open("user added successfully", "Ok", {
              duration: 5000,
            });
          })
      )
    }
  }

  // getUserEmailOnKeyup - Fetches a filtered list of emails matching user input.
  public getUserEmailOnKeyup(query: string): Observable<string[]> {
    if (!query) {
      return new Observable((observer) => observer.next([]));
    }
    return this.adminservice.getEmail({ typed_text: query }).pipe(
      map((res: unknown) => {
        if (Array.isArray(res)) {
          return res.map((user: { email: string }) => user.email);
        }
        return [];
      })
    );
  }

  public close(): void {
    this.dialogRef.close();
  }
  onDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }
}
